import axios from 'axios'
import braintree from 'braintree-web'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import gaEvent from 'utilities/tracking'
import ErrorScreen from 'components/ErrorScreen'
import OrderSummaryPaymentScreen from 'components/OrderSummaryPaymentScreen'
import * as checkoutActions from 'containers/Checkout/actions'
import BrandedButton from 'components/BrandedButton'
import BackButton from 'components/BackButton'
import cvc3Icon from 'cvc-3-icon.png'

import {
  DEFAULT_RESPONSE_ERROR,
  DEFAULT_RESPONSE_DECLINED,
  STAGE_PERSONAL,
  API_PAYMENT_URL,
} from 'containers/Checkout/constants'
import { calculateUpfront } from './utils'
import styles from './styles.scss'

const PaymentStage = ({
  title,
  text,
  textNocharge,
  buttonText,
  termsText,
  nbnTermsText,
  paymentProcessing,
  paymentError,
  paymentErrorMessage,
  paymentDeclined,
  paymentDeclinedMessage,
  totalUpfront,
  stageSettings,
  summarySettings,
  paymentSubmit,
  transitionToStage,
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [brainTreeError, setBrainTreeError] = useState(false)
  const [clientToken, setClientToken] = useState()
  const [reqId, setReqId] = useState()
  const [hostedFields, setHostedFields] = useState()
  const [termsChecked, setTermsChecked] = useState(false)

  const showFeedbackClass = (element, show) => {
    // Adding css to hosted fields to show or hide invalid feedback
    if (show) {
      element.classList.add('show-feedback')
      element.classList.remove('hide-feedback')
    } else {
      element.classList.remove('show-feedback')
      element.classList.add('hide-feedback')
    }
  }

  const setValidityClasses = (element, validity) => {
    // Adding css to hosted fields to show or remove custom validity
    if (!validity) {
      element.classList.add('custom-invalid')
    } else {
      element.classList.remove('custom-invalid')
    }
  }

  // generate braintree clientToken on initial load
  useEffect(() => {
    axios
      .get(API_PAYMENT_URL)
      .then((response) => {
        if (response.data && response.data.success) {
          setClientToken(response.data.token)
          setReqId(response.data.reqId)
        } else {
          setIsLoading(false)
          setBrainTreeError(true)
          gaEvent('Error', {
            event_category: 'Payment',
            event_label: 'Error Loading Client Token',
            non_interaction: true,
          })
        }
      })
      .catch(() => {
        setIsLoading(false)
        setBrainTreeError(true)
        gaEvent('Error', {
          event_category: 'Payment',
          event_label: 'Init Network Error',
          non_interaction: true,
        })
      })
  }, [])

  // once braintree clientToken is available then generate braintree hostedFields
  useEffect(() => {
    if (clientToken) {
      // 1. Create braintree client
      braintree.client.create({ authorization: clientToken })
        .catch(() => {
          setIsLoading(false)
          setBrainTreeError(true)
        })
        .then((clientInstance) => {
          // 2. Create braintree hosted fields
          const options = {
            client: clientInstance,
            styles: {
              input: {
                'font-size': '1rem',
                'font-family': 'Boing", Helvetica, Arial, sans-serif',
                color: '#888888',
              },
            },
            fields: {
              cardholderName: {
                selector: '#cc-name',
              },
              number: {
                selector: '#cc-number',
                supportedCardBrands: {
                  'diners-club': false,
                },
              },
              cvv: {
                selector: '#cc-cvc',
              },
              expirationDate: {
                selector: '#cc-expiration',
                placeholder: 'MM / YY',
              },
            },
          }
          return braintree.hostedFields.create(options)
        }).then((instance) => {
          // 3. Add event listeners to braintree hosted fields
          setIsLoading(false)
          setHostedFields(instance)
          // treat empty hosted fields as invalid
          instance.on('empty', (e) => {
            const element = e.fields[e.emittedBy].container
            setValidityClasses(element, false)
            showFeedbackClass(element.nextSibling, true)
          })
          // when hosted fields toggle between invalid and valid
          instance.on('validityChange', (e) => {
            const field = e.fields[e.emittedBy]
            const element = field.container
            if (field.isValid) {
              setValidityClasses(element, true)
              showFeedbackClass(element.nextSibling, false)
            } else {
              setValidityClasses(element, false)
              showFeedbackClass(element.nextSibling, true)
            }
          })
        })
        .catch(() => {
          setBrainTreeError(true)
          setIsLoading(false)
          gaEvent('Error', {
            event_category: 'Payment',
            event_label: 'Error Loading Hosted Fields',
            non_interaction: true,
          })
        })
    }
  }, [clientToken])

  const onBack = () => {
    transitionToStage(STAGE_PERSONAL)
  }

  const isFormValid = () => {
    const currState = hostedFields.getState()
    return Object.keys(currState.fields).reduce((result, key) => {
      if (!currState.fields[key].isValid) {
        const element = currState.fields[key].container
        setValidityClasses(element, false)
        showFeedbackClass(element.nextSibling, true)
        const formElement = element.parentElement
        formElement.scrollIntoView()
        return result && false
      }

      return result && true
    }, true)
  }

  const onSubmit = (e) => {
    e.preventDefault()

    if (isFormValid()) {
      const options = { vault: true }
      hostedFields.tokenize(options)
        // 1. tokenize hosted fields
        .catch(() => {
          setBrainTreeError(true)
          gaEvent('Error', {
            event_category: 'Payment',
            event_label: 'Error Tokenize Hosted Fields',
            non_interaction: true,
          })
        })
        // 2. Valuting the card and submit payment
        .then((tokenizePayload) => {
          paymentSubmit(reqId, tokenizePayload.nonce)
        })
    }
  }

  const onTermsSelect = () => {
    setTermsChecked(prevState => !prevState)
  }

  const toRender = (
    <div className={styles.stageWrapper}>
      <div className={styles.summaryWrapper}>
        <OrderSummaryPaymentScreen
          stageSettings={stageSettings}
          summarySettings={summarySettings}
        />
      </div>
      <div className={styles.paymentWrapper}>
        <h2>{title}</h2>
        {/* eslint-disable-next-line react/no-danger */}
        <div dangerouslySetInnerHTML={{ __html: text }} />
        {/* eslint-disable-next-line react/no-danger */}
        {!totalUpfront && <div dangerouslySetInnerHTML={{ __html: textNocharge }} />}

        <form className={styles.creditCardForm} method="POST" action="/" onSubmit={onSubmit}>
          <input type="hidden" name="payment_method_nonce" />

          {/* braintree need the the hostedField defined as div hence the eslint disable */}
          { /* eslint-disable-next-line jsx-a11y/label-has-for */ }
          <label htmlFor="cc-name" className={styles.label} id="cc-name-label">
            <span className={styles.labelText}>Cardholder Name</span>
            <div className="hostedField" id="cc-name" />
            <span className="hide-feedback">Please enter a valid card holder name</span>
          </label>

          { /* eslint-disable-next-line jsx-a11y/label-has-for */ }
          <label htmlFor="cc-number" className={styles.label} id="cc-number-label">
            <span className={styles.labelText}>Credit Card Number</span>
            <div className="hostedField" id="cc-number" />
            <span className="hide-feedback">Please enter a valid credit card number</span>
          </label>

          { /* eslint-disable-next-line jsx-a11y/label-has-for */ }
          <label htmlFor="cc-expiration" className={styles.label} id="cc-expiration-label">
            <span className={styles.labelText}>Expiration</span>
            <div className="hostedField" id="cc-expiration" />
            <span className="hide-feedback">Please enter a valid expiry date</span>
          </label>

          { /* eslint-disable-next-line jsx-a11y/label-has-for */ }
          <label htmlFor="cc-cvc" className={styles.label} id="cc-cvc-label">
            <span className={styles.labelText}>CVN</span>
            <div className="hostedField" id="cc-cvc">
              <img className="cardIcon" src={cvc3Icon} alt="cvn3" />
            </div>
            <span className="hide-feedback">Please enter a valid CVV</span>
          </label>

          <div className={classNames(styles.termsConditions, { [styles.complete]: termsChecked })}>
            { /* eslint-disable-next-line jsx-a11y/label-has-for */ }
            <label className={styles.termsTextAsLabel} htmlFor="checkTerms" id="checkTerms-label">
              <button
                className={styles.checkIcon}
                type="button"
                onClick={onTermsSelect}
                id="checkTerms"
                disabled={isLoading}
              />
              {/* eslint-disable-next-line react/no-danger */}
              <div className={styles.termsText} dangerouslySetInnerHTML={{ __html: termsText }} />
              {!termsChecked && <span className={styles.required}>This field is required</span>}
            </label>
          </div>

          <div className={styles.buttonWrapper}>
            <BackButton className={styles.backButton} onClick={onBack} />
            <BrandedButton
              className={styles.submitButton}
              type="submit"
              labelText={buttonText}
              iconName="creditCard"
              loading={paymentProcessing || isLoading}
              disabled={!termsChecked || brainTreeError}
            />
          </div>
        </form>

        <div className={styles.nbnDisclaimer}>
          {/* eslint-disable-next-line react/no-danger */}
          <div className={styles.nbnDisclaimerText} dangerouslySetInnerHTML={{ __html: nbnTermsText }} />
        </div>

        { paymentError &&
          <div className={styles.error}>
            <i className={styles.errorIcon} />
            {/* eslint-disable-next-line react/no-danger */}
            <div className={styles.errorText} dangerouslySetInnerHTML={{ __html: paymentErrorMessage }} />
          </div>
            }
        { paymentDeclined &&
          <div className={styles.error}>
            <i className={styles.errorIcon} />
            {/* eslint-disable-next-line react/no-danger */}
            <div className={styles.errorText} dangerouslySetInnerHTML={{ __html: paymentDeclinedMessage }} />
          </div>
            }

      </div>
      <div className="clearfix" />
    </div>
  )

  if (brainTreeError) {
    return <ErrorScreen />
  }

  return toRender
}

PaymentStage.propTypes = {
  // From redux
  paymentError: PropTypes.bool,
  paymentDeclined: PropTypes.bool,
  paymentProcessing: PropTypes.bool,
  totalUpfront: PropTypes.number,
  // From Wagtail
  title: PropTypes.string,
  text: PropTypes.string,
  textNocharge: PropTypes.string,
  buttonText: PropTypes.string,
  termsText: PropTypes.string,
  nbnTermsText: PropTypes.string,
  paymentErrorMessage: PropTypes.string,
  paymentDeclinedMessage: PropTypes.string,
  stageSettings: PropTypes.object,
  summarySettings: PropTypes.object,
  // Redux functions
  paymentSubmit: PropTypes.func,
  transitionToStage: PropTypes.func,
}

PaymentStage.defaultProps = {
  paymentError: false,
  paymentDeclined: false,
  paymentProcessing: false,
  totalUpfront: 0,
  title: 'Payment',
  text: null,
  textNocharge: null,
  buttonText: 'CONFIRM & PAY',
  termsText: 'Terms and conditions text',
  nbnTermsText: 'Speeds may vary',
  paymentErrorMessage: DEFAULT_RESPONSE_ERROR,
  paymentDeclinedMessage: DEFAULT_RESPONSE_DECLINED,
  stageSettings: {},
  summarySettings: {},
  paymentSubmit: f => f,
  transitionToStage: f => f,
}

const mapStateToProps = state => ({
  paymentError: state.checkoutReducer.paymentError,
  paymentDeclined: state.checkoutReducer.paymentDeclined,
  paymentProcessing: state.checkoutReducer.paymentProcessing,
  totalUpfront: calculateUpfront(state),
})

const mapDispatchToProps = dispatch => bindActionCreators(
  {
    paymentSubmit: checkoutActions.paymentSubmit,
    transitionToStage: checkoutActions.transitionToStage,
  },
  dispatch
)

export default connect(mapStateToProps, mapDispatchToProps)(PaymentStage)



// WEBPACK FOOTER //
// ./containers/stages/PaymentStage/index.jsx